import { useMutation } from '@tanstack/react-query';
import { PricingCalculationResponse } from '../types';
import { ProposalFormData, ProposalGenerationResponse } from './types';

const PROPOSAL_API_URL =
	'https://script.google.com/macros/s/AKfycbyC-XSu0SXNJpImCIRwSSbXMKcXL8nUTpso09UgyEPF5MPmVQy0Y-ieWY3XZF1Zfc9W4w/exec';

interface ProposalPayload extends ProposalFormData {
	pricingData: PricingCalculationResponse;
}

/**
 * Helper to format numbers as comma-separated strings for the API
 */
const formatForApi = (value: number | undefined): string => {
	const num = value ?? 0;
	return num.toLocaleString('en-US', {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	});
};

/**
 * Hook to generate proposals using Google Apps Script
 */
export function useProposalGeneration() {
	return useMutation<ProposalGenerationResponse, Error, ProposalPayload>({
		mutationFn: async (payload: ProposalPayload) => {
			const pd = payload.pricingData;

			// Build flat payload structure with all fields at root level (matching original implementation)
			const flatPayload = {
				// Pricing breakdown fields (with commas)
				basePrice: formatForApi(pd.basePrice),
				extraUserCost: formatForApi(pd.extraUserCost),
				onboardingFee: formatForApi(pd.onboardingFee),
				facebookAdsFee: formatForApi(pd.facebookAdsFee),
				googleLSAFee: formatForApi(pd.googleLSAFee),
				googlePpcFee: formatForApi(pd.googlePpcFee),
				customContentFee: formatForApi(pd.customContentFee),
				subtotal: formatForApi(pd.subtotal),
				renewalSubtotal: formatForApi(pd.renewalAnnualTotal),
				discountAmount: formatForApi(pd.discountAmount),
				totalBeforeTaxes: formatForApi(pd.totalBeforeTaxes),
				taxAmount: formatForApi(pd.taxAmount),
				annualTotal: formatForApi(pd.annualTotal),
				quarterlyTotal: formatForApi(pd.quarterlyTotal),
				splitTotal: formatForApi(pd.splitTotal),
				renewalAnnualTotal: formatForApi(pd.renewalAnnualTotal),
				renewalQuarterlyTotal: formatForApi(pd.renewalQuarterlyTotal),
				// Additional calculated fields
				pricingMonthly: formatForApi(pd.pricingMonthly),
				impFeePrice: formatForApi(pd.impFeePrice ?? pd.onboardingFee),
				pricingAnnual: formatForApi(pd.pricingAnnual ?? pd.subtotal),
				discountSubtotal: formatForApi(pd.discountSubtotal),
				totalCost: formatForApi(pd.totalCost ?? pd.annualTotal),
				quarterlyCost: formatForApi(pd.quarterlyCost ?? pd.quarterlyTotal),
				splitBillingCost: pd.splitBilling ? formatForApi(pd.splitBillingCost) : '',
				mrrAmount: formatForApi(pd.mrrAmount ?? pd.pricingMonthly),
				// Keep non-numeric fields
				offerUrl: pd.offerUrl,
				cacheInfo: pd.cacheInfo,
				// Form fields from pricingData
				planType: pd.planType ?? '',
				numUsers: String(pd.numUsers ?? ''),
				discountPercentage: `${pd.discountPercentage ?? 0}%`,
				state: pd.state ?? ' ',
				billingCycle: pd.billingCycle ?? 'Annual',
				fbAdsChecked: pd.fbAdsChecked ?? false,
				fbAdsPremiumChecked: pd.fbAdsPremiumChecked ?? false,
				gLSAChecked: pd.gLSAChecked ?? false,
				googlePpcChecked: pd.googlePpcChecked ?? false,
				customContentChecked: pd.customContentChecked ?? false,
				customContentNonprofit: pd.customContentNonprofit ?? false,
				handwritten: pd.handwritten ?? false,
				splitBilling: pd.splitBilling ?? false,
				special: payload.special ?? '',
				quarterlyVerbiage: pd.quarterlyVerbiage ?? '',
				splitBillingVerbiage: pd.splitBillingVerbiage ?? '',
				planAddons: pd.planAddons ?? '',
				// Additional fields expected by API
				businessPackage: pd.planType ?? '',
				numberUsers: String(pd.numUsers ?? ''),
				// Form fields from proposal form
				proposalType: payload.proposalType,
				managerEmail: '',
				managerName: '',
				managerTitle: '',
				industry: payload.industry,
				prospectDM: payload.prospectName ?? '',
				prospectDMTitle: payload.prospectTitle ?? '',
				prospectCompany: payload.companyName ?? '',
				websiteAddon: payload.websiteAddon ?? '',
			};

			const response = await fetch(PROPOSAL_API_URL, {
				body: JSON.stringify(flatPayload),
				headers: {
					'Content-Type': 'text/plain',
				},
				method: 'POST',
			});

			if (!response.ok) {
				throw new Error(`Failed to generate proposal: ${response.statusText}`);
			}

			const data = await response.json();
			return data;
		},
	});
}
